import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import config from '../config';

const GroupInvitePage = () => {
  const { groupId } = useParams();
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        console.log('🔍 Fetching public group data for ID:', groupId);
        // Use the correct API URL from config
        const apiUrl = `${config.API_URL}/groups/${groupId}/public`;
        console.log('📡 Making API call to:', apiUrl);
        
        const response = await fetch(apiUrl);
        console.log('📡 API Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ API Error:', errorText);
          throw new Error('Group not found');
        }
        
        const result = await response.json();
        console.log('✅ API Result:', result);
        
        if (result.success) {
          setGroupData(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch group data');
        }
      } catch (err) {
        console.error('💥 Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      console.log('🚀 Starting fetch for group ID:', groupId);
      fetchGroupData();
    } else {
      console.error('❌ No group ID provided');
      setError('Invalid group ID');
      setLoading(false);
    }
  }, [groupId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Invitation Not Found</h1>
          <p className="text-gray-600">This invitation link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
              {groupData?.logo_url && groupData.logo_url.toLowerCase() !== 'placeholder' ? (
                <img 
                  src={groupData.logo_url} 
                  alt={`${groupData.name} logo`}
                  className="w-16 h-16 object-contain rounded-full"
                />
              ) : (
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-purple-600">
                    {groupData?.name?.charAt(0) || 'G'}
                  </span>
                </div>
              )}
            </div>
            <h1 className="text-white text-xl font-bold mb-2">
              You've been invited to join
            </h1>
            <h2 className="text-white text-2xl font-bold">
              {groupData?.name || 'Loading...'}
            </h2>
          </div>

          {/* Body */}
          <div className="p-6">
            {/* Download Buttons - Changed to clickable buttons that trigger deep link */}
            <div className="space-y-3 mb-6">
              <button
                onClick={() => {
                  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                  // Try deep link first
                  window.location.href = `yesod://prayer-group/invite?code=${groupId}`;
                  // Fallback to app store after delay
                  setTimeout(() => {
                    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
                      window.location.href = 'https://apps.apple.com/us/app/yesod/id6748472269';
                    } else {
                      window.location.href = 'https://apps.apple.com/us/app/yesod/id6748472269';
                    }
                  }, 1500);
                }}
                className="w-full flex items-center justify-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer bg-white"
              >
                <img 
                  src="/assets/AppStore.png"
                  alt="App Store"
                  className="w-6 h-6 object-contain"
                />
                <span className="font-medium text-gray-900">Download for iOS</span>
              </button>
              
              <button
                onClick={() => {
                  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                  // Try deep link first
                  window.location.href = `yesod://prayer-group/invite?code=${groupId}`;
                  // Fallback to app store after delay
                  setTimeout(() => {
                    if (/android/i.test(userAgent)) {
                      window.location.href = 'https://play.google.com/store/apps/details?id=us.yesod.app';
                    } else {
                      window.location.href = 'https://play.google.com/store/apps/details?id=us.yesod.app';
                    }
                  }, 1500);
                }}
                className="w-full flex items-center justify-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer bg-white"
              >
                <img 
                  src="/assets/Googleplay.png"
                  alt="Google Play"
                  className="w-6 h-6 object-contain"
                />
                <span className="font-medium text-gray-900">Download for Android</span>
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-purple-50 rounded-lg p-6 text-center">
              <h3 className="font-semibold text-purple-800 mb-4 text-lg">How to join:</h3>
              <ol className="text-purple-700 space-y-2">
                <li className="flex items-center justify-center gap-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <span>Download the Yesod app</span>
                </li>
                <li className="flex items-center justify-center gap-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <span>Search for <strong>{groupData?.name || 'this group'}</strong></span>
                </li>
                <li className="flex items-center justify-center gap-3">
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <span>Request to join</span>
                </li>
              </ol>
            </div>
            
            {/* Open App Button - Matches working prayer-group-details.html implementation */}
            <div className="mt-4">
              <button
                onClick={() => {
                  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
                  
                  // EXACT same logic as prayer-group-details.html joinGroupInApp()
                  window.location.href = `yesod://prayer-group/invite?code=${groupId}`;
                  
                  // If app doesn't open, redirect to appropriate app store after delay
                  setTimeout(() => {
                    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
                      window.location.href = 'https://apps.apple.com/us/app/yesod/id6748472269';
                    } else if (/android/i.test(userAgent)) {
                      window.location.href = 'https://play.google.com/store/apps/details?id=us.yesod.app';
                    } else {
                      // Default to iOS App Store for desktop/unknown devices
                      window.location.href = 'https://apps.apple.com/us/app/yesod/id6748472269';
                    }
                  }, 1500);
                }}
                className="w-full flex items-center justify-center p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold cursor-pointer border-none"
              >
                Open App
              </button>
            </div>
            
            {/* View in Web Button */}
            <div className="mt-2">
              <a
                href={`${config.FRONTEND_URL || window.location.origin}/#/community/${groupId}`}
                className="w-full flex items-center justify-center p-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors font-normal text-sm no-underline"
              >
                View on Web
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 text-center">
            <p className="text-xs text-gray-500">
              Powered by <span className="font-semibold">Yesod</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupInvitePage;